import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Delete } from '@mui/icons-material';
import { CartItem } from '../types';
import { colors, borderRadius, spacing, typography, shadows } from '../styles/theme';

const CartItemCard = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.md};
  padding: ${spacing.md};
  background: ${colors.backgroundSecondary};
  border-radius: ${borderRadius.lg};
  margin-bottom: ${spacing.sm};
  box-shadow: ${shadows.sm};
`;

const ItemImage = styled.img`
  width: 60px;
  height: 60px;
  border-radius: ${borderRadius.md};
  object-fit: cover;
  flex-shrink: 0;
`;

const ItemInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ItemName = styled.h4`
  font-family: ${typography.fontFamily};
  font-size: ${typography.fontSize.md};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.textPrimary};
  margin: 0 0 ${spacing.xs} 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ItemPrice = styled.span`
  font-family: ${typography.fontFamily};
  font-size: ${typography.fontSize.sm};
  color: ${colors.primary};
  font-weight: ${typography.fontWeight.semibold};
`;

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
`;

const QuantityButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: ${borderRadius.full};
  background: ${colors.primary};
  color: ${colors.textInverse};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${typography.fontSize.md};
  font-weight: ${typography.fontWeight.bold};
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.primaryDark};
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  &:disabled {
    background: ${colors.textTertiary};
    cursor: not-allowed;
    transform: none;
  }
`;

const QuantityDisplay = styled.span`
  font-family: ${typography.fontFamily};
  font-size: ${typography.fontSize.md};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.textPrimary};
  min-width: 24px;
  text-align: center;
`;

const RemoveButton = styled.button`
  color: ${colors.error};
  font-size: ${typography.fontSize.lg};
  padding: ${spacing.xs};
  border-radius: ${borderRadius.md};
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 59, 48, 0.1);
  }

  &:active {
    transform: scale(0.9);
  }
`;

const TotalSection = styled.div`
  margin-top: ${spacing.lg};
  padding-top: ${spacing.lg};
  border-top: 1px solid ${colors.borderLight};
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${spacing.sm};
`;

const TotalLabel = styled.span`
  font-family: ${typography.fontFamily};
  font-size: ${typography.fontSize.md};
  color: ${colors.textSecondary};
`;

const TotalAmount = styled.span`
  font-family: ${typography.fontFamily};
  font-size: ${typography.fontSize.xl};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.primary};
`;

const OrderButton = styled(motion.button)`
  width: 100%;
  padding: ${spacing.md};
  background: ${colors.primary};
  color: ${colors.textInverse};
  border-radius: ${borderRadius.lg};
  font-family: ${typography.fontFamily};
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.semibold};
  margin-top: ${spacing.md};
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.primaryDark};
  }

  &:active {
    transform: scale(0.98);
  }

  &:disabled {
    background: ${colors.textTertiary};
    cursor: not-allowed;
  }
`;

const EmptyCart = styled.div`
  text-align: center;
  padding: ${spacing.xxxl};
  color: ${colors.textTertiary};
`;

const EmptyCartIcon = styled.div`
  font-size: 48px;
  margin-bottom: ${spacing.md};
`;

const EmptyCartText = styled.p`
  font-family: ${typography.fontFamily};
  font-size: ${typography.fontSize.md};
  margin: 0;
`;

interface CartDrawerProps {
  cartItems: CartItem[];
  total: number;
  onUpdateQuantity: (itemId: string, quantity: number) => void;
  onRemoveItem: (itemId: string) => void;
  onOrder: () => void;
}

const CartDrawer: React.FC<CartDrawerProps> = ({
  cartItems,
  total,
  onUpdateQuantity,
  onRemoveItem,
  onOrder
}) => {
  const deliveryFee = total > 500 ? 0 : 100; // Бесплатная доставка от 500₽
  const finalTotal = total + deliveryFee;

  if (cartItems.length === 0) {
    return (
      <EmptyCart>
        <EmptyCartIcon>🛒</EmptyCartIcon>
        <EmptyCartText>Ваша корзина пуста</EmptyCartText>
        <EmptyCartText>Добавьте товары из меню</EmptyCartText>
      </EmptyCart>
    );
  }

  return (
    <div>
      {cartItems.map((item) => (
        <CartItemCard key={item.id}>
          <ItemImage src={item.foodItem.image} alt={item.foodItem.name} />
          <ItemInfo>
            <ItemName>{item.foodItem.name}</ItemName>
            <ItemPrice>{item.foodItem.price}₽</ItemPrice>
          </ItemInfo>

          <QuantityControls>
            <QuantityButton
              onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
              disabled={item.quantity <= 1}
            >
              −
            </QuantityButton>
            <QuantityDisplay>{item.quantity}</QuantityDisplay>
            <QuantityButton
              onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
            >
              +
            </QuantityButton>
          </QuantityControls>

          <RemoveButton onClick={() => onRemoveItem(item.id)}>
            <Delete sx={{ fontSize: 16 }} />
          </RemoveButton>
        </CartItemCard>
      ))}

      <TotalSection>
        <TotalRow>
          <TotalLabel>Сумма товаров:</TotalLabel>
          <TotalAmount>{total}₽</TotalAmount>
        </TotalRow>

        <TotalRow>
          <TotalLabel>Доставка:</TotalLabel>
          <TotalAmount>
            {deliveryFee === 0 ? 'Бесплатно' : `${deliveryFee}₽`}
          </TotalAmount>
        </TotalRow>

        {deliveryFee > 0 && (
          <TotalRow>
            <TotalLabel style={{ fontSize: '12px', color: colors.textTertiary }}>
              Добавьте товаров на {(500 - total)}₽ для бесплатной доставки
            </TotalLabel>
          </TotalRow>
        )}

        <OrderButton
          whileTap={{ scale: 0.98 }}
          onClick={onOrder}
        >
          Оформить заказ • {finalTotal}₽
        </OrderButton>
      </TotalSection>
    </div>
  );
};

export default CartDrawer;
